import { useCallback, useMemo } from "react";
import {
  DataTable,
  DataTableCellContent,
  DataTableColumnSettings,
  DataTableEmpty,
  DataTableToolbar,
  DataTableToolbarLeft,
  DataTableToolbarActions,
  Badge,
  Box,
  Text,
  Icon,
  MenuItem,
  MenuSeparator,
  Separator,
  cn,
  createColumnHelper,
  renderDefaultGroupActions,
  type DataTableColumnDef,
  type DataTableGroupType,
  type BulkAction,
  type SortingState,
  type OnChangeFn,
} from "@salestrak/ui";
import type { ReactNode } from "react";

import { ExpandableSearch } from "@salestrak/ui";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { connectionRows, type CustomFieldRow, CustomFieldsListDataDocument } from "#app/lib/data";
import { EntityTag } from "#app/components/entity";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { useFieldTypeOrder } from "#app/lib/hooks/use-field-type-order";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const h = createColumnHelper<CustomFieldRow>();

// Shared by the table (sticky pinning) and the column settings panel (locked
// rows). The panel ignores structural ids like "select".
const CUSTOM_FIELDS_PINNED_COLUMN_IDS = ["select", "name"];

// Duplicate and delete are the row-action menu's, not the columns' — the menu
// closes over them where it is rendered.
function useCustomFieldColumns({
  onEdit,
}: {
  onEdit: (field: CustomFieldRow) => void;
}): DataTableColumnDef<CustomFieldRow>[] {
  return useMemo<DataTableColumnDef<CustomFieldRow>[]>(
    () => [
      h.hoverAction({
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        content: (field) => (
          <Text size="sm" weight="medium" truncate={1}>
            {field.name}
          </Text>
        ),
        onClickAction: (field) => onEdit(field),
        actionLabel: (field) => `Edit ${field.name}`,
      }),
      h.display({
        id: "createdBy",
        header: "Created By",
        enableSorting: false,
        meta: { minWidth: 180 },
        cell: ({ row }) => {
          const user = row.original.createdBy;
          if (!user) {
            return (
              <DataTableCellContent>
                <Text size="sm" variant="muted">Unknown</Text>
              </DataTableCellContent>
            );
          }
          return (
            <DataTableCellContent>
              <EntityTag
                entity={{ kind: "user", id: user.userId, user }}
              />
            </DataTableCellContent>
          );
        },
      }),
      h.date({
        accessorKey: "lastUpdateDate",
        header: "Updated",
      }),
    ],
    [onEdit],
  );
}

// ---------------------------------------------------------------------------
// Per-group query helpers
// ---------------------------------------------------------------------------

const getConnection = (data: any) => data?.customFields;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type CustomFieldsTableProps = Omittable<{
  where: Record<string, unknown>;
  // Undefined when the list defers ordering to the API (`deferOrderBy`) and the
  // user has not sorted — the variable is then simply omitted.
  orderBy: Record<string, string> | undefined;
  groupByField?: "none" | "type";
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onEdit: (field: CustomFieldRow) => void;
  onDuplicate: (field: CustomFieldRow) => void;
  onDelete: (field: CustomFieldRow) => void;
  bulkActions?: BulkAction<CustomFieldRow>[];
  toolbarLeft?: ReactNode;
  toolbarActions?: ReactNode;
}>;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomFieldsTable({
  where: whereProp,
  orderBy: orderByProp,
  groupByField = "none",
  sorting,
  onSortingChange,
  searchValue,
  onSearchChange,
  onEdit,
  onDuplicate,
  onDelete,
  bulkActions,
  toolbarLeft,
  toolbarActions,
}: CustomFieldsTableProps) {
  const columns = useCustomFieldColumns({ onEdit });
  const isGrouped = groupByField === "type";
  const tableView = useTableViewSettings("custom-fields");
  // Workspace-scoped Photos flag drops the photos type entirely: no photos
  // group/query when grouped, and photos rows filtered from the flat list (the
  // server `where` can't exclude a type, so the flat path filters client-side).
  const fieldTypeOrder = useFieldTypeOrder();
  const { hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);

  // --- Group query state ---
  const { states, registerState, getGroupProps } = useGroupStates<CustomFieldRow>();

  // Per-type where clauses — stable references (one per field type)
  const perTypeWhere = useMemo(
    () => Object.fromEntries(fieldTypeOrder.map((type) => [type, { ...whereProp, type: { eq: type } }])),
    [whereProp, fieldTypeOrder],
  );

  // Build table groups based on grouping mode
  const groups = useMemo<DataTableGroupType<CustomFieldRow>[]>(() => {
    if (!isGrouped) {
      const all = getGroupProps("__all__");
      return [{
        key: "__all__",
        label: "All",
        totalCount: states.get("__all__")?.total,
        ...all,
        // The flat query can't exclude photos server-side, so drop them here.
        data: photosEnabled
          ? all.data
          : all.data.filter((f) => f.type !== "photos"),
      }];
    }

    const result: DataTableGroupType<CustomFieldRow>[] = [];
    for (const type of fieldTypeOrder) {
      const state = states.get(type);
      if (state && (state.total > 0 || state.loading)) {
        const meta = getFieldTypeMeta(type);
        result.push({
          key: type,
          label: meta.label,
          totalCount: state.total,
          ...getGroupProps(type),
        });
      }
    }

    // Include any types not in our known order (future-proofing)
    for (const [key, state] of states) {
      if (!fieldTypeOrder.includes(key) && key !== "__all__" && (state.total > 0 || state.loading)) {
        result.push({
          key,
          label: key,
          totalCount: state.total,
          ...getGroupProps(key),
        });
      }
    }

    // Empty state — single fallback group
    if (result.length === 0) {
      return [{ key: "__all__", label: "All", data: [] as CustomFieldRow[], loading: true }];
    }

    return result;
  }, [isGrouped, states, getGroupProps, fieldTypeOrder, photosEnabled]);

  // Custom group title renderer with colored icon
  const renderGroupTitle = useCallback((groupKey: string) => {
    if (groupKey === "__all__") return null;
    const meta = getFieldTypeMeta(groupKey);
    return (
      <Badge color={meta.color}>
        <Icon name={meta.icon} size="xs" />
        <Text size="sm" weight="medium">{meta.label}</Text>
      </Badge>
    );
  }, []);

  return (
    <>
      {!isGrouped && (
        <GroupQueryProvider<CustomFieldRow>
          groupKey="__all__"
          document={CustomFieldsListDataDocument}
          variables={{ where: whereProp, orderBy: orderByProp, limit: 50 }}
          getConnection={getConnection}
          onStateChange={registerState}
        />
      )}

      {isGrouped && fieldTypeOrder.map((type) => (
        <GroupQueryProvider<CustomFieldRow>
          key={type}
          groupKey={type}
          document={CustomFieldsListDataDocument}
          variables={{ where: perTypeWhere[type], orderBy: orderByProp, limit: 50 }}
          getConnection={getConnection}
          onStateChange={registerState}
        />
      ))}

      <Separator />
      <Box paddingTop="sm" paddingBottom="sm">
        <DataTableToolbar>
          <DataTableToolbarLeft>
            {toolbarLeft}
          </DataTableToolbarLeft>
          <DataTableToolbarActions>
            <ExpandableSearch
              onValueChange={onSearchChange}
              placeholder="Search custom fields..."
              size="xs"
            />
            <DataTableColumnSettings
              columns={columns}
              pinnedColumnIds={CUSTOM_FIELDS_PINNED_COLUMN_IDS}
              {...tableView.settingsProps}
            />
            {toolbarActions && (
              <>
                {/* A vertical separator takes its height from a class. */}
                <Separator orientation="vertical" className="h-4" />
                {toolbarActions}
              </>
            )}
          </DataTableToolbarActions>
        </DataTableToolbar>
      </Box>

      <Box direction="column" fill="flex">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(r) => r.customFieldId}
          sorting={sorting}
          onSortingChange={onSortingChange}
          stickyHeader
          density="regular"
          fillColumnId="name"
          pinnedColumnIds={CUSTOM_FIELDS_PINNED_COLUMN_IDS}
          paddingX="lg"
          {...tableView.tableProps}
          bulkActions={bulkActions}
          renderRowActions={(field) => (
            <>
              <MenuItem onClick={() => onDuplicate(field)}>
                <Icon name="copy" size="lg" color="muted" />
                <Text size="sm">Duplicate</Text>
              </MenuItem>
              <MenuItem onClick={() => onEdit(field)}>
                <Icon name="pencil" size="lg" color="muted" />
                <Text size="sm">Edit</Text>
              </MenuItem>
              <MenuSeparator />
              <MenuItem variant="destructive" onClick={() => onDelete(field)}>
                <Icon name="trash-2" size="lg" color="destructive" />
                <Text size="sm" variant="destructive">Delete...</Text>
              </MenuItem>
            </>
          )}
          groupTitle={isGrouped ? renderGroupTitle : undefined}
          groupActions={isGrouped ? renderDefaultGroupActions : undefined}
          emptyState={
            <DataTableEmpty
              message={
                searchValue
                  ? "No custom fields match your search"
                  : "No custom fields yet"
              }
            />
          }
        />
      </Box>
    </>
  );
}
